import { useTranslation } from 'react-i18next'
import type { ComplianceDocumentsLocaleKey } from '@artem-lex/compliance-documents'
import { Button } from '@/components/ui/button'
import { SUPPORTED_LANGUAGES } from '@/shared/config/languages'
import {
  STANDARD_ENTITY_ORIGINAL_VIEW,
  type EntityDisplayLocale,
} from '@/shared/lib/standardEntityLocalizations'
import { cn } from '@/shared/lib/utils'

type StandardEntityLocaleToggleProps = {
  availableLocales: ComplianceDocumentsLocaleKey[]
  selectedLocale: EntityDisplayLocale
  onSelectLocale: (locale: ComplianceDocumentsLocaleKey) => void
  className?: string
}

const supportedLanguageByValue = new Map(
  SUPPORTED_LANGUAGES.map((language) => [language.value, language])
)

export function StandardEntityLocaleToggle({
  availableLocales,
  selectedLocale,
  onSelectLocale,
  className,
}: StandardEntityLocaleToggleProps) {
  const { t } = useTranslation('standardsLibrary')

  if (availableLocales.length === 0) {
    return null
  }

  return (
    <div className={cn('flex flex-wrap justify-end gap-1', className)}>
      {availableLocales.map((locale) => {
        const language = supportedLanguageByValue.get(locale)
        if (language == null) {
          return null
        }

        const isActive =
          selectedLocale !== STANDARD_ENTITY_ORIGINAL_VIEW &&
          selectedLocale === locale

        return (
          <Button
            key={locale}
            type='button'
            size='sm'
            variant={isActive ? 'default' : 'outline'}
            className='h-7 gap-1 px-2 text-xs font-normal'
            aria-pressed={isActive}
            aria-label={t('detail.entityLocale.showInLanguage', {
              language: language.label,
            })}
            onClick={() => onSelectLocale(locale)}
          >
            <span aria-hidden>{language.flag}</span>
            {language.label}
          </Button>
        )
      })}
    </div>
  )
}
